import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Send, Trash2, Plug, CheckCircle2, X } from 'lucide-react';
import api from '../lib/axios';

interface BotConfig {
  id: string;
  botUsername: string | null;
  deliveryMode: 'WEBHOOK' | 'POLLING';
  tokenSet: boolean;
  webhookUrl: string | null;
  defaultJournalCode: string | null;
  defaultCashAccount: string | null;
  defaultBankAccount: string | null;
  defaultChequeAccount: string | null;
  defaultIncomeAccount: string | null;
  baseCurrency: string | null;
  active: boolean;
}

interface Sender {
  id: string;
  chatId: number;
  displayName: string | null;
  userId: string | null;
  role: 'ACCOUNTANT' | 'ADMIN' | 'SUPER_ADMIN';
  active: boolean;
}

type BotForm = {
  token: string;
  deliveryMode: 'WEBHOOK' | 'POLLING';
  defaultJournalCode: string;
  defaultCashAccount: string;
  defaultBankAccount: string;
  defaultChequeAccount: string;
  defaultIncomeAccount: string;
  baseCurrency: string;
  active: boolean;
};

const DEFAULT_FORM: BotForm = {
  token: '',
  deliveryMode: 'WEBHOOK',
  defaultJournalCode: '',
  defaultCashAccount: '530',
  defaultBankAccount: '512',
  defaultChequeAccount: '511',
  defaultIncomeAccount: '756',
  baseCurrency: 'EUR',
  active: true,
};

export default function TelegramPanel({ slug, onClose }: { slug: string; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<BotForm>({ ...DEFAULT_FORM });
  const [loaded, setLoaded] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [newSender, setNewSender] = useState({ chatId: '', displayName: '', role: 'ACCOUNTANT' });

  const config = useQuery({
    queryKey: ['telegram', slug],
    queryFn: () =>
      api
        .get<BotConfig>(`/tenants/${slug}/telegram`)
        .then((r) => r.data)
        .catch((e) => {
          if (e?.response?.status === 404) return null;
          throw e;
        }),
  });

  // Hydrate the form once, from the loaded config.
  if (config.data && !loaded) {
    const c = config.data;
    setForm({
      token: '',
      deliveryMode: c.deliveryMode,
      defaultJournalCode: c.defaultJournalCode ?? '',
      defaultCashAccount: c.defaultCashAccount ?? '530',
      defaultBankAccount: c.defaultBankAccount ?? '512',
      defaultChequeAccount: c.defaultChequeAccount ?? '511',
      defaultIncomeAccount: c.defaultIncomeAccount ?? '756',
      baseCurrency: c.baseCurrency ?? 'EUR',
      active: c.active,
    });
    setLoaded(true);
  }

  const senders = useQuery({
    queryKey: ['telegram-senders', slug],
    queryFn: () => api.get<Sender[]>(`/tenants/${slug}/telegram/senders`).then((r) => r.data),
    enabled: !!config.data,
  });

  const save = useMutation({
    mutationFn: () =>
      api.put(`/tenants/${slug}/telegram`, {
        token: form.token.trim() || null,
        deliveryMode: form.deliveryMode,
        defaultJournalCode: form.defaultJournalCode.trim() || null,
        defaultCashAccount: form.defaultCashAccount.trim() || null,
        defaultBankAccount: form.defaultBankAccount.trim() || null,
        defaultChequeAccount: form.defaultChequeAccount.trim() || null,
        defaultIncomeAccount: form.defaultIncomeAccount.trim() || null,
        baseCurrency: form.baseCurrency.trim() || null,
        active: form.active,
      }),
    onSuccess: () => {
      setForm((f) => ({ ...f, token: '' }));
      setFeedback(t('telegram.saved'));
      qc.invalidateQueries({ queryKey: ['telegram', slug] });
      qc.invalidateQueries({ queryKey: ['telegram-senders', slug] });
    },
    onError: () => setFeedback(t('telegram.saveError')),
  });

  const test = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; username?: string }>(`/tenants/${slug}/telegram/test`).then((r) => r.data),
    onSuccess: (r) =>
      setFeedback(r.ok ? t('telegram.testOk', { username: r.username ?? '' }) : t('telegram.testFail')),
    onError: () => setFeedback(t('telegram.testFail')),
  });

  const registerWebhook = useMutation({
    mutationFn: () =>
      api
        .post<{ ok: boolean; description?: string }>(`/tenants/${slug}/telegram/register-webhook`)
        .then((r) => r.data),
    onSuccess: (r) =>
      setFeedback(
        r.ok
          ? t('telegram.webhookOk')
          : r.description
            ? t('telegram.webhookFailReason', { reason: r.description })
            : t('telegram.webhookFail'),
      ),
    onError: (e: any) =>
      setFeedback(
        e?.response?.data?.message
          ? t('telegram.webhookFailReason', { reason: e.response.data.message })
          : t('telegram.webhookFail'),
      ),
  });

  const addSender = useMutation({
    mutationFn: () =>
      api.post(`/tenants/${slug}/telegram/senders`, {
        chatId: Number(newSender.chatId),
        displayName: newSender.displayName.trim() || null,
        role: newSender.role,
      }),
    onSuccess: () => {
      setNewSender({ chatId: '', displayName: '', role: 'ACCOUNTANT' });
      qc.invalidateQueries({ queryKey: ['telegram-senders', slug] });
    },
  });

  const removeSender = useMutation({
    mutationFn: (id: string) => api.delete(`/tenants/${slug}/telegram/senders/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['telegram-senders', slug] }),
  });

  const c = config.data;

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="reveal card mt-8 w-full max-w-3xl p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="eyebrow flex items-center gap-2">
              <Send size={14} /> {t('telegram.title')}
            </p>
            <h2 className="page-title mt-1 text-xl">{slug}</h2>
            {c?.botUsername && (
              <p className="mt-1 text-sm text-ink-muted">@{c.botUsername}</p>
            )}
          </div>
          <button onClick={onClose} className="btn btn-secondary">
            <X size={16} /> {t('common.close')}
          </button>
        </div>

        {config.isLoading && <p className="text-ink-muted">{t('common.loading')}</p>}

        {!config.isLoading && (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="field-label">{t('telegram.token')}</label>
                <input
                  type="password"
                  value={form.token}
                  onChange={(e) => setForm((f) => ({ ...f, token: e.target.value }))}
                  className="input"
                  placeholder={c?.tokenSet ? t('telegram.tokenSet') : t('telegram.tokenPlaceholder')}
                  autoComplete="off"
                  spellCheck={false}
                />
                <p className="mt-1 text-xs text-ink-muted">{t('telegram.tokenHint')}</p>
              </div>
              <div>
                <label className="field-label">{t('telegram.deliveryMode')}</label>
                <select
                  value={form.deliveryMode}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, deliveryMode: e.target.value as 'WEBHOOK' | 'POLLING' }))
                  }
                  className="input"
                >
                  <option value="WEBHOOK">{t('telegram.modeWebhook')}</option>
                  <option value="POLLING">{t('telegram.modePolling')}</option>
                </select>
              </div>
              <div>
                <label className="field-label">{t('telegram.baseCurrency')}</label>
                <input
                  value={form.baseCurrency}
                  onChange={(e) => setForm((f) => ({ ...f, baseCurrency: e.target.value }))}
                  className="input"
                />
              </div>
              <div>
                <label className="field-label">{t('telegram.journalCode')}</label>
                <input
                  value={form.defaultJournalCode}
                  onChange={(e) => setForm((f) => ({ ...f, defaultJournalCode: e.target.value }))}
                  className="input"
                  placeholder="BQ"
                />
              </div>
              <div>
                <label className="field-label">{t('telegram.incomeAccount')}</label>
                <input
                  value={form.defaultIncomeAccount}
                  onChange={(e) => setForm((f) => ({ ...f, defaultIncomeAccount: e.target.value }))}
                  className="input"
                />
              </div>
              <div>
                <label className="field-label">{t('telegram.cashAccount')}</label>
                <input
                  value={form.defaultCashAccount}
                  onChange={(e) => setForm((f) => ({ ...f, defaultCashAccount: e.target.value }))}
                  className="input"
                />
              </div>
              <div>
                <label className="field-label">{t('telegram.bankAccount')}</label>
                <input
                  value={form.defaultBankAccount}
                  onChange={(e) => setForm((f) => ({ ...f, defaultBankAccount: e.target.value }))}
                  className="input"
                />
              </div>
              <div>
                <label className="field-label">{t('telegram.chequeAccount')}</label>
                <input
                  value={form.defaultChequeAccount}
                  onChange={(e) => setForm((f) => ({ ...f, defaultChequeAccount: e.target.value }))}
                  className="input"
                />
              </div>
              <label className="flex items-center gap-2 md:col-span-2">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                />
                <span className="text-sm text-ink">{t('telegram.activeBot')}</span>
              </label>
            </div>

            {c?.webhookUrl && (
              <p className="mt-3 break-all text-xs text-ink-muted">
                {t('telegram.webhookUrl')} : <code>{c.webhookUrl}</code>
              </p>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button onClick={() => save.mutate()} disabled={save.isPending} className="btn btn-primary">
                {save.isPending ? t('common.saving') : t('common.save')}
              </button>
              <button
                onClick={() => test.mutate()}
                disabled={test.isPending || !c?.tokenSet}
                className="btn btn-secondary"
              >
                <CheckCircle2 size={16} /> {t('telegram.test')}
              </button>
              <button
                onClick={() => registerWebhook.mutate()}
                disabled={registerWebhook.isPending || !c?.tokenSet}
                className="btn btn-secondary"
              >
                <Plug size={16} /> {t('telegram.registerWebhook')}
              </button>
              {feedback && <span className="text-sm font-medium text-ink-soft">{feedback}</span>}
            </div>

            {/* Sender allowlist */}
            <div className="mt-8">
              <h3 className="mb-3 font-semibold text-ink">{t('telegram.senders')}</h3>
              <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-4">
                <input
                  value={newSender.chatId}
                  onChange={(e) => setNewSender((s) => ({ ...s, chatId: e.target.value }))}
                  className="input"
                  placeholder={t('telegram.chatId')}
                  inputMode="numeric"
                />
                <input
                  value={newSender.displayName}
                  onChange={(e) => setNewSender((s) => ({ ...s, displayName: e.target.value }))}
                  className="input"
                  placeholder={t('telegram.displayName')}
                />
                <select
                  value={newSender.role}
                  onChange={(e) => setNewSender((s) => ({ ...s, role: e.target.value }))}
                  className="input"
                >
                  <option value="ACCOUNTANT">ACCOUNTANT</option>
                  <option value="ADMIN">ADMIN</option>
                  <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                </select>
                <button
                  onClick={() => addSender.mutate()}
                  disabled={addSender.isPending || !newSender.chatId.trim()}
                  className="btn btn-primary"
                >
                  {t('telegram.addSender')}
                </button>
              </div>

              {(senders.data ?? []).length === 0 ? (
                <p className="text-sm text-ink-muted">{t('telegram.noSender')}</p>
              ) : (
                <div className="card overflow-hidden">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('telegram.chatId')}</th>
                        <th>{t('telegram.displayName')}</th>
                        <th>{t('telegram.role')}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {(senders.data ?? []).map((s) => (
                        <tr key={s.id}>
                          <td className="text-ink-soft">{s.chatId}</td>
                          <td>{s.displayName || '-'}</td>
                          <td>
                            <span className="pill pill-neutral">{s.role}</span>
                          </td>
                          <td className="text-right">
                            <button
                              onClick={() => removeSender.mutate(s.id)}
                              className="btn btn-ghost text-expense"
                              title={t('common.delete')}
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
